/**
 * IPFS gateway fetch with byte-counting via ReadableStream.
 *
 * We don't run an in-browser IPFS node — that's overkill for the demo.
 * We just hit a public gateway and tee the stream to count bytes as
 * they arrive on the wire (the BandwidthHUD reads from the same counter).
 */

export const DEFAULT_GATEWAY = "https://ipfs.io/ipfs/";

/** Global counter incremented by every fetched chunk. */
export class WireCounter {
  private _bytes = 0;
  private listeners = new Set<(b: number) => void>();
  get bytes(): number {
    return this._bytes;
  }
  add(n: number) {
    this._bytes += n;
    for (const l of this.listeners) l(this._bytes);
  }
  reset() {
    this._bytes = 0;
    for (const l of this.listeners) l(0);
  }
  subscribe(fn: (b: number) => void): () => void {
    this.listeners.add(fn);
    fn(this._bytes);
    return () => this.listeners.delete(fn);
  }
}

export const wireCounter = new WireCounter();

/** Fetch a URL (CID via gateway, or local placeholder) as Uint8Array, tallying bytes into the global counter. */
export async function fetchCounted(url: string, signal?: AbortSignal): Promise<Uint8Array> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
  if (!res.body) {
    const buf = new Uint8Array(await res.arrayBuffer());
    wireCounter.add(buf.byteLength);
    return buf;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.byteLength;
      wireCounter.add(value.byteLength);
    }
  }
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.byteLength;
  }
  return out;
}

/** Resolve a manifest entry to a fetchable URL. CIDs go through the gateway; relative paths stay relative. */
export function resolveChunkUrl(entry: { cid?: string; url?: string }, gateway = DEFAULT_GATEWAY): string {
  if (entry.url) return entry.url;
  if (entry.cid) return `${gateway}${entry.cid}`;
  throw new Error("manifest entry has neither cid nor url");
}
